<template>
  <div class="layout-center-wrap">
    <div class="page-tit__wrap">
      <h1 class="page-tit__sub">취약점 정보</h1>
      <h2 class="page-tit">CVE 게시판</h2>
    </div>

    <!-- filter-panel -->
    <section class="filter-panel">
      <div class="filter-panel__header">
        <span class="filter-panel__title">검색 필터</span>
        <button
          type="button"
          class="filter-reset"
          :disabled="!hasActiveFilter"
          @click="resetAndReload"
        >
          필터 초기화
        </button>
      </div>

      <!-- CNA -->
      <div class="filter-panel__row">
        <div class="filter-panel__label">대한민국 CNA</div>
        <div class="filter-panel__chips">
          <label
            class="filter-chip filter-chip--all"
            :class="{ 'filter-chip--checked': allCnaChecked }"
          >
            <input
              type="checkbox"
              :checked="allCnaChecked"
              @change="selectAllCnas"
            />
            <span class="filter-chip__dot"></span>
            <span>전체</span>
          </label>
          <template v-if="state.cnas && state.cnas.length">
            <label
              v-for="cna in state.cnas"
              :key="cna.id"
              class="filter-chip"
              :class="{ 'filter-chip--checked': isCnaChecked(cna.id) }"
            >
              <input
                type="checkbox"
                :checked="isCnaChecked(cna.id)"
                @change="toggleCna(cna.id, $event.target.checked)"
              />
              <span class="filter-chip__dot"></span>
              <span>{{ cna.name }}</span>
              <strong>{{ numberFormat.format(cna.count) }}</strong>
            </label>
          </template>
          <span v-else class="status-line">CNA 정보를 불러오는 중입니다.</span>
        </div>
      </div>

      <!-- Severity — DB에서 동적 구성 -->
      <div class="filter-panel__row">
        <div class="filter-panel__label">CVSS 위험도</div>
        <div class="filter-panel__chips">
          <label
            v-for="sev in state.severityFilters"
            :key="sev.id"
            class="filter-chip filter-chip--status"
            :class="[
              sev.className,
              { 'filter-chip--checked': searchInfo.severity.includes(sev.id) },
            ]"
          >
            <input
              type="checkbox"
              :checked="searchInfo.severity.includes(sev.id)"
              @change="toggleSeverity(sev.id, $event.target.checked)"
            />
            <span class="filter-chip__dot"></span>
            <span>{{ sev.label }}</span>
          </label>
        </div>
      </div>

      <!-- KEV -->
      <div class="filter-panel__row">
        <div class="filter-panel__label">주요 고위험</div>
        <div class="filter-panel__chips">
          <label
            class="filter-chip filter-chip--accent"
            :class="{ 'filter-chip--checked': searchInfo.highKev }"
          >
            <input
              type="checkbox"
              :checked="searchInfo.highKev"
              @change="toggleKev($event.target.checked)"
            />
            <span class="filter-chip__dot"></span>
            <span>CVSS High 이상 + CISA KEV 등재</span>
          </label>
        </div>
      </div>

      <!-- 검색어 -->
      <div class="filter-panel__row filter-panel__row--search">
        <div class="filter-panel__label">키워드</div>
        <div class="filter-panel__search">
          <select
            v-model="searchInfo.field"
            class="filter-search__select"
            aria-label="키워드 구분"
          >
            <option value="ALL">전체</option>
            <option value="TITLE">제목</option>
            <option value="PRODUCT">제품</option>
            <option value="CVE">CVE</option>
            <option value="CNA">CNA</option>
          </select>
          <input
            v-model="searchInfo.q"
            class="filter-search__input"
            type="search"
            placeholder="검색어를 입력해주세요"
            @keyup.enter="getList(1)"
          />
        </div>
      </div>

      <div class="filter-panel__footer">
        <button
          type="button"
          class="btn btn--line btn--white_light btn--m"
          :disabled="!hasActiveFilter"
          @click="resetAndReload"
        >
          초기화
        </button>
        <button type="button" class="btn btn--blue btn--m" @click="getList(1)">
          <i class="svg_icon icon_search white"></i>검색
        </button>
      </div>
    </section>

    <!-- 결과 수 -->
    <div class="list-table__top">
      <p class="list-table__total">
        총
        <span v-if="pagingInfo.total === null" class="spin"></span>
        <template v-else>{{ pagingInfo.total }}</template> 건
      </p>
    </div>

    <!-- 테이블 -->
    <div class="entry-table-wrap">
      <table class="entry-table">
        <colgroup>
          <col class="entry-table__col-id" />
          <col class="entry-table__col-title" />
          <col class="entry-table__col-info" />
          <col class="entry-table__col-badge" />
          <col class="entry-table__col-tag" />
          <col class="entry-table__col-date" />
        </colgroup>
        <thead>
          <tr>
            <th>CVE</th>
            <th>제목</th>
            <th>제품</th>
            <th>CVSS</th>
            <th>KEV</th>
            <th>등록일</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="items && items.length">
            <tr
              v-for="item in items"
              :key="item.cveId"
              class="entry-table__row"
              @click="goToDetail(item.cveId)"
            >
              <td class="entry-table__col-id">{{ item.cveId }}</td>
              <td class="entry-table__col-title">
                <span class="entry-table__title-main">{{
                  item.title || item.cveId
                }}</span>
              </td>
              <td class="entry-table__col-info">
                <span class="ellipsis">
                  {{ item.product || item.cwe || "-" }}</span
                >
              </td>
              <td class="entry-table__col-badge">
                <span
                  v-if="item.cvssScore != null && item.cvssScore !== ''"
                  :class="[
                    'level-badge',
                    `level-badge--${severityVariant(item.cvssSeverity)}`,
                  ]"
                >
                  {{ formatScore(item.cvssScore) }} ({{
                    severityName(item.cvssSeverity)
                  }})
                </span>
                <span v-else class="level-badge level-badge--unknown"
                  >평가 전</span
                >
              </td>
              <td class="entry-table__col-tag">
                <span v-if="item.kev" class="tag-badge">CISA KEV</span>
                <span v-else class="tag-none">-</span>
              </td>
              <td class="entry-table__col-date">
                {{ formatDate(item.datePublished || item.dateUpdated) }}
              </td>
            </tr>
          </template>
          <tr v-else>
            <td colspan="6" class="entry-table__empty">
              {{
                isLoading
                  ? "데이터 조회중입니다. 잠시만 기다려주십시오."
                  : "조회 결과가 없습니다."
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Paging
      v-model="pagingInfo.currentPage"
      :totalItems="pagingInfo.total == null ? -1 : pagingInfo.total"
      :visibleCount="pagingInfo.limitNum"
      @update:modelValue="getPage"
    />
  </div>
</template>

<script>
import dc from "@/component/DataChk.js";
import dt from "@/component/DateTime.js";
import paging from "@/component/Paging.js";
import tb from "@/component/Table.js";
import { computed, inject, onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useCveFormat } from "./UseCveFormat";

const numberFormat = new Intl.NumberFormat("ko-KR");

export default {
  setup() {
    const axios = inject("axios");
    const router = useRouter();
    const store = useStore();

    const showLoading = () => store.dispatch("loading/show");
    const hideLoading = () => store.dispatch("loading/hide");

    // ── 상태 ────────────────────────────────────────────────────────────────
    const pagingInfo = reactive({
      currentPage: 1,
      prePage: 1,
      perPage: 10,
      limitNum: 5,
      type: 1,
      total: null,
      pageNum: 0,
      doubleTab: false,
    });

    const searchInfo = ref({
      sortBy: "_id",
      order: -1,
      skipCount: 0,
      limit: 0,
      preKey: "",
      nextKey: "",
      changePerpage: false,
      q: "",
      field: "ALL",
      cna: [],
      severity: [],
      highKev: false,
    });

    const savedSearchInfo = ref({ ...searchInfo.value });

    const state = reactive({
      cnas: [],
      severityFilters: [],
    });

    const items = ref([]);

    // ── Composable ───────────────────────────────────────────────────────────
    const { severityVariant, severityName, formatScore, formatDate } =
      useCveFormat();

    // ── Computed ─────────────────────────────────────────────────────────────
    const isLoading = computed(() => store.state.loading.isLoading);
    const allCnaChecked = computed(
      () =>
        state.cnas.length > 0 &&
        searchInfo.value.cna.length === state.cnas.length
    );

    const hasActiveFilter = computed(
      () =>
        searchInfo.value.cna.length > 0 ||
        searchInfo.value.severity.length > 0 ||
        searchInfo.value.highKev ||
        !!searchInfo.value.q
    );

    // ── 목록 조회 ─────────────────────────────────────────────────────────────
    const getList = (type) => {
      searchInfo.value = dc.trimObject(searchInfo.value);
      const axiosSearchInfo = tb.getSearchInfo(
        type,
        searchInfo,
        savedSearchInfo
      );

      pagingInfo.type = type;
      if (type !== 2) {
        pagingInfo.total = null;
        paging
          .total("/core/pu/view/cve/count/get", axiosSearchInfo)
          .then((res) => {
            pagingInfo.total = res;
          });
      }

      paging
        .getPageItems("/core/pu/view/cve/get", axiosSearchInfo, pagingInfo)
        .then((res) => {
          items.value = res;
          pagingInfo.doubleTab = true;
        });

      tb.saveSearchInfo(type, searchInfo, savedSearchInfo);
    };

    // ── CNA 필터 로드 ─────────────────────────────────────────────────────────
    const loadCnaFilters = () => {
      axios
        .post("/core/pu/view/cve/cna/get")
        .then((res) => {
          if (res.data.resType === "RES_OK") {
            state.cnas = res.data.resList;
          }
        })
        .catch((err) => console.error("[CveBoard] CNA 필터 로드 실패:", err));
    };

    // ── Severity 필터 로드 (cvss.severity DB 기반) ─────────────x───────────────
    const getSeverities = () => {
      axios
        .get("/core/pu/view/cve/severity/get")
        .then((res) => {
          if (res.data.resType === "RES_OK") {
            state.severityFilters = [
              ...res.data.resList
                .slice()
                .sort((a, b) => b.min - a.min) // CRITICAL → LOW 순
                .map((s) => ({
                  id: s.severity,
                  label:
                    s.severity.charAt(0) + s.severity.slice(1).toLowerCase(),
                  className: `filter-chip--${severityVariant(s.severity)}`,
                })),
              {
                id: "NONE",
                label: "None",
                className: `filter-chip--${severityVariant("NONE")}`,
              },
            ];
          }
        })
        .catch((err) => console.error("[CveBoard] Severity 로드 실패:", err));
    };

    // ── 페이지네이션 ──────────────────────────────────────────────────────────
    const getPage = (page) => {
      pagingInfo.currentPage = page;
      pagingInfo.pageNum = page - pagingInfo.currentPage;
      pagingInfo.doubleTab = false;
      getList(2);
    };

    // ── CNA 체크 헬퍼 ────────────────────────────────────────────────────────
    const isCnaChecked = (id) => searchInfo.value.cna.includes(id);

    const selectAllCnas = () => {
      searchInfo.value.cna = allCnaChecked.value
        ? []
        : state.cnas.map((c) => c.id);
    };

    const toggleCna = (id, checked) => {
      if (checked) {
        searchInfo.value.cna.push(id);
      } else {
        searchInfo.value.cna = searchInfo.value.cna.filter((c) => c !== id);
      }
    };

    const toggleSeverity = (id, checked) => {
      if (checked) {
        searchInfo.value.severity.push(id);
      } else {
        searchInfo.value.severity = searchInfo.value.severity.filter(
          (s) => s !== id
        );
      }
    };

    const toggleKev = (checked) => {
      searchInfo.value.highKev = checked;
    };

    const resetAndReload = () => {
      searchInfo.value.cna = [];
      searchInfo.value.severity = [];
      searchInfo.value.highKev = false;
      searchInfo.value.q = "";
      searchInfo.value.field = "ALL";
    };

    // ── 상세 이동 ─────────────────────────────────────────────────────────────
    const goToDetail = (cveId) => {
      searchInfo.value.cveId = cveId;
      tb.saveStateForDetail("cveBoard", searchInfo.value, pagingInfo);
      router.push({ name: "CveBoardDetail", query: { id: cveId } });
    };

    // ── 날짜 ─────────────────────────────────────────────────────────────────
    const convertDateFormat = (date) => dt.dateFormat(date);

    // life cycle 정의
    onMounted(() => {
      loadCnaFilters();
      getSeverities();

      const retrievedState = tb.retrieveAndClearState(
        "cveBoard",
        "CveBoardDetail",
        searchInfo,
        savedSearchInfo,
        pagingInfo
      );
      const {
        type: type,
        searchInfo: parsedSearchInfo,
        pagingInfo: parsedPagingInfo,
      } = retrievedState;

      getList(type);
    });

    // html에서 사용될 데이터 정의
    return {
      numberFormat,
      pagingInfo,
      searchInfo,
      state,
      items,
      isLoading,
      allCnaChecked,
      hasActiveFilter,
      isCnaChecked,
      selectAllCnas,
      toggleCna,
      toggleSeverity,
      toggleKev,
      resetAndReload,
      getList,
      getPage,
      goToDetail,
      convertDateFormat,
      severityVariant,
      severityName,
      formatScore,
      formatDate,
    };
  },
};
</script>
